import { Button, Flex, Form, Modal } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// components
import FormElement from 'components/formElement/formElement';
import PhoneInputComponent, { PhoneInputReturn } from 'components/phoneInput/phoneInput';
import Icon from 'utils/icon';

// services
import { checkPhoneValidation } from 'utils/validators/phone';

// models
import { FormProps } from 'antd/es/form/Form';
import FormNumberElement from 'components/formNumberElement/formNumberElement';
import { onCheckNationalId } from 'modules/main/actions/commonServices';
import { isRTL } from 'core/services/language';
import { PrivateEquityShareholder } from '../models/privateEquityIssuance';

// components
import SuspendedTooltip from 'components/suspendedTooltip/suspendedTooltip';

interface PrivateEquityShareholderModalProps {
	open: boolean;
	onCancel: () => void;
	onConfirm: (values: PrivateEquityShareholder) => void;
	shareholderData?: PrivateEquityShareholder;
	shareholders: PrivateEquityShareholder[];
	remainingShares: number;
}

const PrivateEquityShareholderModal: FunctionComponent<PrivateEquityShareholderModalProps> = ({
	open,
	onCancel,
	onConfirm,
	shareholderData,
	shareholders,
	remainingShares,
}) => {
	const { t } = useTranslation();

	const [shareholderForm] = Form.useForm();

	const [searching, setSearching] = useState(false);
	const [shareholder, setShareholder] = useState<PrivateEquityShareholder | undefined>(shareholderData);

	useEffect(() => {
		if (shareholderData) {
			shareholderForm.setFieldsValue(shareholderData);
			setShareholder(shareholderData);
		}
	}, [shareholderData]);

	const onChangePhone = ({ number, dialCode }: PhoneInputReturn) => {
		shareholderForm.setFieldsValue({
			phone_number: number,
			phone_country_code: dialCode,
		});
	};

	const validatePhone = (_: any, value: any) => {
		if (checkPhoneValidation(shareholderForm.getFieldValue('phone_country_code'), value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.PHONE_INVALID')));
		else return Promise.reject();
	};

	const checkNationalIdHandler = async (value: string) => {
		if (value.length === 10) {
			try {
				const isDuplicate = shareholders.find((sh) => sh.national_id === value && sh.national_id !== shareholderData?.national_id);
				if (isDuplicate) return;

				setSearching(true);
				const response = await onCheckNationalId(value);

				if (response.success && response.data?.shareholder) {
					setShareholder({
						national_id: value,
						name: isRTL() ? response.data.shareholder.name : (response.data.shareholder.name_en ?? response.data.shareholder.name),
						phone_number: response.data.shareholder.phone_number,
						phone_country_code: response.data.shareholder.phone_country_code,
						owned_shares: shareholder?.owned_shares || 0,
					});

					shareholderForm.setFieldsValue({
						name: isRTL() ? response.data.shareholder.name : (response.data.shareholder.name_en ?? response.data.shareholder.name),
						phone_number: response.data.shareholder.phone_number,
						phone_country_code: response.data.shareholder.phone_country_code,
					});
				}

				setSearching(false);
			} catch (error: any) {}
		} else {
			setSearching(false);
			setShareholder(undefined);
			shareholderForm.setFieldsValue({
				name: '',
				phone_number: '',
				phone_country_code: '',
			});
		}
	};

	const saveShareholderHandler: FormProps<PrivateEquityShareholder>['onFinish'] = async (values) => {
		onConfirm(values);

		setTimeout(() => {
			shareholderForm.resetFields();
			setShareholder(undefined);
		}, 300);
	};

	const cancelShareholderHandler = () => {
		onCancel();
		shareholderForm.resetFields();
		setShareholder(undefined);
	};

	return (
		<Modal
			open={open}
			style={{
				top: 0,
				marginTop: 20,
				maxHeight: 'calc(100vh - 45px)',
				overflow: 'auto',
				paddingBottom: 0,
				borderRadius: 'var(--radius)',
			}}
			title={
				<Flex gap={10}>
					<Icon name="info" width={40} height={40} />
					<div>
						<h6 className="mb-0">{t('SHAREHOLDERS_REGISTRY.ADD_NEW_SHAREHOLDER')}</h6>
						<span className="text-light" style={{ fontSize: 14 }}>
							{t('SHAREHOLDERS_REGISTRY.ADD_ALL_DATA_MANUALLY')}
						</span>
					</div>
				</Flex>
			}
			footer={[
				<Button key="discard" type="link" danger onClick={cancelShareholderHandler}>
					{t('COMMON.CANCEL')}
				</Button>,
				<SuspendedTooltip key="submit">
					<Button key="submit" type="primary" onClick={shareholderForm.submit}>
						{shareholderData ? t('COMMON.UPDATE') : t('COMMON.ADD')}
					</Button>
				</SuspendedTooltip>,
			]}
			onCancel={cancelShareholderHandler}>
			<Form form={shareholderForm} onFinish={saveShareholderHandler}>
				<FormElement
					name="national_id"
					label={t('COMMON.NATIONAL_ID')}
					fullWidth
					loading={searching}
					type="number"
					onChange={checkNationalIdHandler}
					disabled={!!shareholderData}
					rules={[
						{ required: true, message: t('COMMON.FIELD_REQUIRED') },
						{ len: 10, message: t('COMMON.NATIONAL_ID_LENGTH') },
						{
							validator: (_, value) => {
								const isDuplicate = shareholders.find((sh) => sh.national_id == value);
								if (isDuplicate && !shareholderData) return Promise.reject(new Error(t('ACTIONS.SHAREHOLDER_ALREADY_ADDED')));
								return Promise.resolve();
							},
						},
					]}
				/>

				<FormElement
					name="name"
					label={t('COMMON.NAME')}
					fullWidth
					type="text"
					disabled={!!shareholder?.name || !!shareholderData}
					rules={[
						{ required: true, message: t('COMMON.FIELD_REQUIRED') },
						{ min: 12, message: t('COMMON.FIELD_LENGTH_VALIDATION') },
					]}
				/>

				<Form.Item
					name="phone_number"
					style={{ maxWidth: 'unset' }}
					rules={[{ required: true, message: t('COMMON.PHONE_REQUIRED') }, { validator: validatePhone }]}>
					<PhoneInputComponent
						fullWidth
						onChange={onChangePhone}
						userPhoneNumber={shareholder?.phone_number}
						userDialCode={shareholder?.phone_country_code}
						disabled={!!shareholder || !!shareholderData}
					/>
				</Form.Item>

				<Form.Item name="phone_country_code" hidden noStyle />

				<FormNumberElement
					fullWidth
					label={t('ACTIONS.OWNED_SHARES')}
					name="owned_shares"
					step={1}
					rules={[
						{ type: 'number', min: 1, message: t('COMMON.FIELD_REQUIRED') },
						{
							type: 'number',
							max: shareholderData ? shareholderData.owned_shares + remainingShares : remainingShares,
							message: t('ACTIONS.SHARES_EXCEEDING_LIMIT', {
								limit: shareholderData ? shareholderData.owned_shares + remainingShares : remainingShares,
							}),
						},
					]}
				/>
			</Form>
		</Modal>
	);
};

export default PrivateEquityShareholderModal;
